import React, { useMemo } from "react";
import { Button, Form } from "react-bootstrap";
import { DashLg, PlusLg } from "react-bootstrap-icons";
import type { IDishDetailsFormValues, TSupplement } from "@/types";
import { useFormikContext } from "formik";
import {
  changeMultiOptionQty,
  getOptionQuantity,
  getSelectedOptionsCountInGroup,
  setSingleRequired,
  toggleSingleOptional,
} from "@/pages/menuItemsPage/helpers/supplementsHelpers";
import RequiredChoiceBadge from "@/pages/menuItemsPage/components/requiredChoiceBadge";
import { normalizeText, safeNumber } from "@/pages/menuItemsPage/helpers";
import { useTranslation } from "react-i18next";
import { pickLocalizedText } from "@/utils/languageVersions/pickLocalizedText";

interface DishSupplementsSelectorProps {
  supplements: TSupplement[];
  currency: string;
  className?: string;
}

const DishSupplementsSelector: React.FC<DishSupplementsSelectorProps> = ({
  supplements,
  currency,
  className = "",
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<IDishDetailsFormValues>();

  const setSupplementsValue = (
    nextSupplements: IDishDetailsFormValues["supplements"]
  ) => {
    setFieldValue("supplements", nextSupplements);
  };

  const sortedGroups = useMemo(() => {
    return [...supplements]
      .map((group) => {
        const groupName = pickLocalizedText(
          group.groupName,
          group.groupNameOrigin
        );
        if (!groupName) return null;

        const options = (group.options ?? [])
          .map((option) => {
            const optionName = pickLocalizedText(
              option.name,
              option.nameOrigin
            );
            if (!optionName) return null;

            return {
              ...option,
              localizedName: optionName,
            };
          })
          .filter(
            (option): option is NonNullable<typeof option> => option != null
          );

        if (options.length === 0) return null;

        return {
          ...group,
          groupName,
          options,
        };
      })
      .filter((group): group is NonNullable<typeof group> => group != null)
      .sort((a, b) =>
        normalizeText(a.groupName).localeCompare(normalizeText(b.groupName))
      );
  }, [supplements]);

  if (!sortedGroups.length) return null;

  return (
    <div className={className}>
      {sortedGroups.map((group) => {
        const groupName = normalizeText(group.groupName);
        const limit = safeNumber(group.maxCountOnOrder);
        const isRequired = Boolean(group.isRequireOnOrder);

        const isRadioMode = limit === 1 && isRequired;
        const isSingleOptionalMode = limit === 1 && !isRequired;
        const isMultiMode = limit > 1;

        const selectedOptionsCount = getSelectedOptionsCountInGroup(
          values.supplements,
          group.id
        );

        const hasSelection = selectedOptionsCount > 0;

        const isGroupLimitReached =
          isMultiMode && limit > 0 && selectedOptionsCount >= limit;

        const hintText =
          limit <= 0
            ? null
            : limit === 1
              ? isRequired
                ? t("Choose 1 option")
                : t("You can choose 1 option")
              : isRequired
                ? t("Choose from 1 to {limit} options", { limit })
                : t("Choose up to {limit} options", { limit });

        const singleSelectedOptionId = isSingleOptionalMode
          ? (group.options?.find(
              (o) => getOptionQuantity(values.supplements, group.id, o.id) > 0
            )?.id ?? null)
          : null;

        return (
          <div
            key={group.id}
            id={`supp-group-${group.id}`}
            className="mt-8 mt-md-9"
          >
            <h5 className="fw-bold fs-20">{groupName}</h5>

            <div className="d-flex align-items-center justify-content-between gap-2">
              {hintText ? (
                <span className="fs-14 text-grey-text">{hintText}</span>
              ) : null}
              <RequiredChoiceBadge
                isRequired={isRequired}
                hasSelection={hasSelection}
              />
            </div>

            <div className="mt-3 d-flex flex-column gap-3">
              {(group.options ?? []).map((option) => {
                const optionName = normalizeText(option.localizedName);
                const optionPrice = safeNumber(option.price);
                const optionQty = getOptionQuantity(
                  values.supplements,
                  group.id,
                  option.id
                );

                const priceText = `${optionPrice.toFixed(2)} ${currency}`;

                const disableMultiPlus =
                  isMultiMode && isGroupLimitReached && optionQty === 0;

                const disableSingleOptionalPlus =
                  isSingleOptionalMode &&
                  singleSelectedOptionId != null &&
                  singleSelectedOptionId !== option.id;

                return (
                  <div
                    key={option.id}
                    className="d-flex align-items-center justify-content-between gap-3"
                  >
                    <div className="d-flex gap-1 fs-14-16">
                      <div className="">{optionName}</div>
                      <div className="fw-bold">{priceText}</div>
                    </div>

                    {isRadioMode ? (
                      <Form.Check className="m-0 d-flex align-items-center justify-content-between gap-2 p-0">
                        <Form.Check.Label
                          htmlFor={`supp-${group.id}-${option.id}`}
                          className="m-0 flex-grow-1"
                        />
                        <Form.Check.Input
                          id={`supp-${group.id}-${option.id}`}
                          type="radio"
                          name={`supp-group-${group.id}`}
                          className="m-0 flex-shrink-0"
                          checked={optionQty > 0}
                          onChange={() => {
                            const next = setSingleRequired(
                              values.supplements,
                              group.id,
                              option.id
                            );
                            setSupplementsValue(next);
                          }}
                        />
                      </Form.Check>
                    ) : null}

                    {isSingleOptionalMode ? (
                      optionQty > 0 ? (
                        <div className="d-inline-flex align-items-center gap-3">
                          <Button
                            type="button"
                            variant="additional-violet"
                            className="p-0 rounded-1 border-0 lh-1"
                            style={{ width: "20px", height: "20px" }}
                            onClick={() => {
                              const next = toggleSingleOptional(
                                values.supplements,
                                group.id,
                                option.id
                              );
                              setSupplementsValue(next);
                            }}
                          >
                            <DashLg size={10} color="#5842BE" />
                          </Button>

                          <span className="fs-14">{optionQty}</span>

                          <Button
                            type="button"
                            variant="additional-violet"
                            className="p-0 rounded-1 border-0 lh-1"
                            style={{ width: "20px", height: "20px" }}
                            disabled
                          >
                            <PlusLg size={10} color="#5842BE" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="additional-violet"
                          className="p-0 rounded-1 border-0 lh-1"
                          style={{ width: "20px", height: "20px" }}
                          disabled={disableSingleOptionalPlus}
                          onClick={() => {
                            const next = toggleSingleOptional(
                              values.supplements,
                              group.id,
                              option.id
                            );
                            setSupplementsValue(next);
                          }}
                        >
                          <PlusLg size={10} color="#5842BE" />
                        </Button>
                      )
                    ) : null}

                    {isMultiMode ? (
                      optionQty > 0 ? (
                        <div className="d-inline-flex align-items-center gap-3">
                          <Button
                            type="button"
                            variant="additional-violet"
                            className="p-0 rounded-1 border-0 lh-1"
                            style={{ width: "20px", height: "20px" }}
                            onClick={() => {
                              const next = changeMultiOptionQty({
                                supplements: values.supplements,
                                supplement: group,
                                optionId: option.id,
                                delta: -1,
                              });
                              setSupplementsValue(next);
                            }}
                          >
                            <DashLg size={10} color="#5842BE" />
                          </Button>

                          <span className="fs-14">{optionQty}</span>

                          <Button
                            type="button"
                            variant="additional-violet"
                            className="p-0 rounded-1 border-0 lh-1"
                            style={{ width: "20px", height: "20px" }}
                            onClick={() => {
                              const next = changeMultiOptionQty({
                                supplements: values.supplements,
                                supplement: group,
                                optionId: option.id,
                                delta: +1,
                              });
                              setSupplementsValue(next);
                            }}
                            disabled={disableMultiPlus}
                          >
                            <PlusLg size={10} color="#5842BE" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="additional-violet"
                          className="p-0 rounded-1 border-0 lh-1"
                          style={{ width: "20px", height: "20px" }}
                          disabled={disableMultiPlus}
                          onClick={() => {
                            const next = changeMultiOptionQty({
                              supplements: values.supplements,
                              supplement: group,
                              optionId: option.id,
                              delta: +1,
                            });
                            setSupplementsValue(next);
                          }}
                        >
                          <PlusLg size={10} color="#5842BE" />
                        </Button>
                      )
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default React.memo(DishSupplementsSelector);
